import { useState, type ReactNode } from "react";
import { FILE_META, fileOf, type FileId } from "./content";
import { FileGlyph, Ic } from "./icons";
import { tx, txr } from "../../lib/tx";

/** "Rules for this simulation": the Sharing Guide window. */
export function GuideBody() {
  return (
    <div className="sis-doc sis-scroll">
      <div className="sis-doc-banner">
        <span className="sis-doc-bic"><Ic n="book" size={20} /></span>
        <div>
          <strong>{tx("Rules for this simulation")}</strong>
          <span>{tx("The Sharing Guide for the workshop. This is not your employer's real policy.")}</span>
        </div>
      </div>

      <section>
        <h4>{tx("Where to share")}</h4>
        <p>{tx("Use the approved work-sharing service on this computer.")}</p>
      </section>

      <section>
        <h4>{tx("Who may see a file")}</h4>
        <ul className="sis-lvls">
          <li>
            <span className="sis-lvl sis-lvl--pub">{tx("Public")}</span>
            <span>{tx("Only content marked approved for public distribution. A public link is allowed when the task needs it.")}</span>
          </li>
          <li>
            <span className="sis-lvl sis-lvl--int">{tx("Internal")}</span>
            <span>{tx("Only the internal audience named in the request.")}</span>
          </li>
          <li>
            <span className="sis-lvl sis-lvl--lim">{tx("Limited")}</span>
            <span>{tx("The named person or the Workshop project team named in the request.")}</span>
          </li>
        </ul>
      </section>

      <section>
        <h4>{tx("What each permission means")}</h4>
        <ul className="sis-rl">
          <li><Ic n="eye" size={14} /> {txr("<0>View</0> means read.", {}, [<b />])}</li>
          <li><Ic n="comment" size={14} /> {txr("<0>Comment</0> adds feedback without changing the file.", {}, [<b />])}</li>
          <li><Ic n="pencil" size={14} /> {txr("<0>Edit</0> allows changes.", {}, [<b />])}</li>
        </ul>
      </section>

      <section>
        <h4>{tx("Before you share")}</h4>
        <ul className="sis-bl">
          <li>{tx("Check access that already exists, not only the new link.")}</li>
          <li>{tx("Match external people using the directory card, including the full address.")}</li>
        </ul>
      </section>

      <section>
        <h4>{tx("Apps")}</h4>
        <ul className="sis-bl">
          <li>
            {txr("<0>Workshop Helper</0> is the approved document tool for this workshop. It may open the selected document only.", {}, [<b />])}
          </li>
          <li>{tx("A broad request to see all files, read mail and send mail is not part of any task here.")}</li>
          <li>{tx("Report a suspicious app approval.")}</li>
        </ul>
      </section>

      <section>
        <h4>{tx("When the work is finished")}</h4>
        <ul className="sis-bl">
          <li>{tx("Remove project access when the work is finished.")}</li>
        </ul>
      </section>
    </div>
  );
}

function Topic({ q, children }: { q: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`sis-topic${open ? " is-open" : ""}`}>
      <button type="button" className="sis-topic-q" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span>{q}</span>
        <Ic n="chevron" size={14} />
      </button>
      <div className="sis-topic-a">
        <div>{children}</div>
      </div>
    </div>
  );
}

export function HelpBody({ onAccess, onApps }: { onAccess: () => void; onApps: () => void }) {
  return (
    <div className="sis-doc sis-scroll">
      <div className="sis-doc-banner is-help">
        <span className="sis-doc-bic"><Ic n="help" size={20} /></span>
        <div>
          <strong>{tx("How can we help?")}</strong>
          <span>{tx("Help explains where things are. It does not share anything for you.")}</span>
        </div>
      </div>
      <Topic q={tx("Where do I start?")}>
        {tx("Open Mail to read the request. Open Files to find the document. Select it, then choose Share.")}
      </Topic>
      <Topic q={tx("How do I see who can already open a file?")}>
        {tx("Select the file in Files and choose Manage access. Each way in is listed on its own row: a direct share, a link, a group, a folder, or a project guest.")}
      </Topic>
      <Topic q={tx("Someone still has access after I shared the file")}>
        {tx("A new share does not cancel older access. Access that comes from a folder is changed by asking the owner for a restricted project location.")}
      </Topic>
      <Topic q={tx("An app is asking for a lot of permissions")}>
        {tx("Approving gives the app everything it lists, until it is removed. Cancel what the task does not need, and report anything suspicious.")}
      </Topic>
      <Topic q={tx("Can I remove a project guest myself?")}>
        {tx("Guests belong to the project, so the owner removes them. Use Ask owner to remove in Manage access.")}
      </Topic>
      <div className="sis-help-act">
        <button type="button" className="sis-btn sis-btn--sm" onClick={onAccess}>
          <Ic n="lock" size={13} /> {tx("Open Manage access")}
        </button>
        <button type="button" className="sis-btn sis-btn--sm" onClick={onApps}>
          <Ic n="plug" size={13} /> {tx("Open Connected apps")}
        </button>
      </div>
    </div>
  );
}

function lineClass(line: string): string {
  if (/^(internal note|draft comment|contact)/i.test(line)) return "sis-page-flag";
  if (/^(approved|marked)/i.test(line)) return "sis-page-stamp";
  return "";
}

/** A read-only document window opened by double-clicking a file. */
export function ViewerBody({
  fileId,
  onShare,
  onManage,
}: {
  fileId: FileId;
  onShare: () => void;
  onManage: () => void;
}) {
  const file = fileOf(fileId);
  const meta = FILE_META[fileId];
  return (
    <div className="sis-view">
      <div className="sis-view-bar">
        <span className="sis-readonly"><Ic n="lock" size={12} /> {tx("Read-only preview")}</span>
        <span className="sis-cmd-sp" />
        <button type="button" className="sis-btn sis-btn--sm sis-btn--primary" onClick={onShare}>
          <Ic n="share" size={13} /> {tx("Share")}
        </button>
        <button type="button" className="sis-btn sis-btn--sm" onClick={onManage}>
          <Ic n="lock" size={13} /> {tx("Manage access")}
        </button>
      </div>
      <div className="sis-view-stage sis-scroll">
        <article className="sis-page" key={fileId}>
          <header>
            <FileGlyph ext={meta.ext} size={34} />
            <h2>{file.name}</h2>
          </header>
          <p className="sis-page-sum">{file.summary}</p>
          {file.preview.map((line) => (
            <p key={line} className={lineClass(line)}>
              {line}
            </p>
          ))}
        </article>
      </div>
    </div>
  );
}
